import { Link } from "@tanstack/react-router";
import { ArrowUpRightIcon, FileTextIcon } from "lucide-react";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrgId } from "../../state/orgs";
import { usePolicies } from "../../state/policies";
import { cn } from "../../lib/utils";
import { matchingPolicyId } from "../regulation/policy/policyLibrarySearch";
import { Button } from "../ui/button";

/** Both policy lists use the same org-scoped resolution and navigation. */
export function InboxPolicyLinks({
  names,
  compact = false,
}: {
  names: readonly string[];
  compact?: boolean;
}) {
  const organizationId = useActiveOrgId();
  return organizationId ? (
    <OrganizationPolicyLinks organizationId={organizationId} names={names} compact={compact} />
  ) : (
    <ul className="space-y-2 text-xs">
      {names.map((name) => (
        <li key={name}>{name}</li>
      ))}
    </ul>
  );
}

function OrganizationPolicyLinks({
  organizationId,
  names,
  compact,
}: {
  organizationId: string;
  names: readonly string[];
  compact: boolean;
}) {
  const { snapshot } = usePolicies(organizationId);
  const org = orgUrlSegment(organizationId);
  return (
    <ul className={compact ? "-mx-1.5 space-y-1" : "mt-3 space-y-1"}>
      {names.map((name) => {
        const policyId = matchingPolicyId(snapshot?.policies ?? [], name);
        return (
          <li key={name}>
            <Button
              variant="ghost"
              size="sm"
              title={policyId ? `Open ${name}` : `Find ${name} in the policy library`}
              className={cn(
                "group/policy h-auto! w-full justify-start gap-2 whitespace-normal text-left font-normal",
                compact
                  ? "px-1.5 py-1.5 text-xs sm:text-xs"
                  : "px-2 py-2.5 text-[13px] sm:text-[13px]",
              )}
              render={
                policyId ? (
                  <Link to="/$org/compliance/policies/$policyId" params={{ org, policyId }} />
                ) : (
                  <Link to="/$org/compliance/policies" params={{ org }} search={{ q: name }} />
                )
              }
            >
              <FileTextIcon className="size-3.5 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 leading-5 group-hover/policy:underline underline-offset-4">
                {name}
              </span>
              <ArrowUpRightIcon className="size-3 shrink-0 text-muted-foreground/60" />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
